import classnames from 'classnames';
import { FormattedMessage } from 'components/shared/Intl';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import { MouseEvent } from 'react';

import { EmphasizeMatchingText } from '../EmphasizeMatchingText/EmphasizeMatchingText';

import '../GenericTypeahead/GenericTypeahead.scss';
import type { ISelectingStatusSelecting } from '../GenericTypeahead/Typeahead.models';

export interface ISalesCounterpartResultPanelOwnProps {
  useBdrId?: boolean;
  hideSalesCounterpartName?: boolean;
}

export type ISalesCounterpartResultPanel = ISelectingStatusSelecting<ISalesCounterpart> &
  ISalesCounterpartResultPanelOwnProps;

export function SalesCounterpartResultPanel({
  availableValues,
  useBdrId = true,
  hideSalesCounterpartName = false,
  selectedIndex,
  searchText,
  onSelectionMouseDown,
}: ISalesCounterpartResultPanel) {
  const displayedResultsCount = availableValues.length;

  return (
    <div className="typeahead-table-result-panel table-borderless bg-primary-alt p-3" id="SalesCounterpartResultPanel">
      <table className="table table-hover table-sm">
        <thead>
          <tr>
            {!hideSalesCounterpartName && (
              <th scope="row">
                <FormattedMessage id="clientName" />
              </th>
            )}
            <th scope="row">
              <FormattedMessage id="client.country" />
            </th>
            {useBdrId && (
              <th scope="row">
                <FormattedMessage id="legalId" />
              </th>
            )}
            <th scope="row">
              <FormattedMessage id="eliotCode" />
            </th>
            <th scope="row" />
          </tr>
        </thead>
        <tbody>
          {availableValues.slice(0, displayedResultsCount).map((counterpart, index) => {
            const isRowSelected = selectedIndex === index;
            return (
              <tr
                key={counterpart.id}
                data-e2e="counterpart-option"
                onMouseDown={handleClick(onSelectionMouseDown, counterpart)}
                ref={isRowSelected ? handleSelectedValue : undefined}
                className={classnames({
                  'table-active': isRowSelected,
                })}
              >
                {!hideSalesCounterpartName && (
                  <td className="p-2">
                    {(searchText && (
                      <EmphasizeMatchingText searchText={searchText}>{counterpart.label}</EmphasizeMatchingText>
                    )) ||
                      counterpart.label}
                  </td>
                )}
                <td className="p-2">{counterpart.countryResidenceCode}</td>
                {useBdrId && (
                  <td className="p-2">
                    <EmphasizeMatchingText searchText={searchText}>{counterpart.id}</EmphasizeMatchingText>
                  </td>
                )}
                <td className="p-2">
                  <EmphasizeMatchingText searchText={searchText}>{counterpart.eliotCode}</EmphasizeMatchingText>
                </td>
                <td className="p-2">{isRowSelected && <i className="icon fw-bold">arrow_forward</i>}</td>
              </tr>
            );
          })}
        </tbody>
        {displayedResultsCount < availableValues.length && (
          <tfoot>
            <tr>
              <td colSpan={3}>{`+ ${availableValues.length - displayedResultsCount}...`}</td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

function handleClick(
  callback: (selectionItem: ISalesCounterpart) => void,
  selectionItem: ISalesCounterpart,
): (e: MouseEvent<HTMLElement>) => void {
  return () => {
    callback(selectionItem);
  };
}

function handleSelectedValue(el: HTMLTableRowElement | null) {
  if (el !== null) {
    el.scrollIntoViewIfNeeded();
  }
}
